/**
 * Tiny hand-written DOM helpers.
 *
 * The frontend is "no framework" by Phase 1 decision; these utilities
 * keep view code terse without bringing in lit / preact / snabbdom.
 *
 * Conventions
 * ───────────
 * - Attributes are passed as an object with the same name as the DOM
 *   property when one exists (`class`, `id`, `href`, `type`, `value`,
 *   `disabled`, `checked`, ...); otherwise they are passed through
 *   `setAttribute`. Boolean attributes such as `disabled`, `checked`,
 *   `hidden` are normalized to a string when truthy and removed when
 *   falsy.
 * - Children may be a string, number, `null`, `false`, a `Node`, or an
 *   array of any of those. This makes templating trivial:
 *     `el("div", { class: "card" }, [el("h2", null, "Title"), " body "])`.
 * - Event handlers receive the native Event; the helper attaches via
 *   `addEventListener` so it composes with bubbling.
 */

export type Child =
  | string
  | number
  | bigint
  | boolean
  | null
  | undefined
  | Node;

export type ChildArray = Child | Child[];

export type Attrs = Record<
  string,
  | string
  | number
  | boolean
  | null
  | undefined
  | EventListener
  | ((e: Event) => void)
  | { [key: string]: unknown }
>;

// Properties that must use `setAttribute` instead of `el[prop] = value`
// because they have no 1:1 DOM property mapping (or the property has a
// different type than the HTML attribute).
const ATTR_ONLY = new Set([
  "aria-label",
  "aria-labelledby",
  "aria-describedby",
  "aria-hidden",
  "aria-current",
  "aria-pressed",
  "role",
  "data-state",
  "data-testid",
  "for",
  "xmlns",
]);

const BOOLEAN_ATTRS = new Set([
  "disabled",
  "checked",
  "hidden",
  "readonly",
  "required",
  "multiple",
  "autofocus",
  "open",
  "selected",
]);

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs?: Attrs | null,
  children?: ChildArray | ChildArray[],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  applyAttrs(node, attrs ?? null);
  appendChildren(node, normalizeChildren(children));
  return node;
}

export function svgEl(
  tag: string,
  attrs?: Attrs | null,
  children?: ChildArray | ChildArray[],
): SVGElement {
  const node = document.createElementNS("http://www.w3.org/2000/svg", tag);
  applyAttrs(node as unknown as HTMLElement, attrs ?? null);
  appendChildren(node as unknown as Node, normalizeChildren(children));
  return node;
}

export function setChildren(parent: Node, children: ChildArray | ChildArray[]): void {
  // Clear existing children without reassigning `innerHTML` (faster +
  // preserves event listeners on `parent` itself).
  while (parent.firstChild) parent.removeChild(parent.firstChild);
  appendChildren(parent, normalizeChildren(children));
}

export function clear(parent: Node): void {
  while (parent.firstChild) parent.removeChild(parent.firstChild);
}

export function h1(text: string): HTMLHeadingElement {
  return el("h1", null, text);
}

export function p(text: string): HTMLParagraphElement {
  return el("p", null, text);
}

export function span(text: string, className?: string): HTMLSpanElement {
  return el("span", className ? { class: className } : null, text);
}

export function fragment(children: ChildArray | ChildArray[]): DocumentFragment {
  const frag = document.createDocumentFragment();
  appendChildren(frag, normalizeChildren(children));
  return frag;
}

// ─── internals ──────────────────────────────────────────────────────────

function normalizeChildren(input: ChildArray | ChildArray[] | undefined): Child[] {
  if (input === undefined || input === null) return [];
  return Array.isArray(input) ? (input as Child[]) : [input as Child];
}

function appendChildren(parent: Node, children: Child[]): void {
  for (const child of children) {
    parent.appendChild(toNode(child));
  }
}

function toNode(child: Child): Node {
  if (child === null || child === undefined || child === false || child === true) {
    return document.createTextNode("");
  }
  if (typeof child === "string" || typeof child === "number" || typeof child === "bigint") {
    return document.createTextNode(String(child));
  }
  return child;
}

function applyAttrs(node: HTMLElement, attrs: Attrs | null): void {
  if (!attrs) return;
  for (const key of Object.keys(attrs)) {
    const value = attrs[key];
    if (value === undefined) continue;
    if (value === null) {
      node.removeAttribute(key);
      continue;
    }
    // Event handlers.
    if (key.startsWith("on") && typeof value === "function") {
      node.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
      continue;
    }
    if (key === "class") {
      if (typeof value === "string") node.className = value;
      continue;
    }
    if (key === "style" && typeof value === "string") {
      node.setAttribute("style", value);
      continue;
    }
    if (ATTR_ONLY.has(key)) {
      if (typeof value === "boolean") {
        if (value) node.setAttribute(key, "");
        else node.removeAttribute(key);
      } else {
        node.setAttribute(key, String(value));
      }
      continue;
    }
    if (BOOLEAN_ATTRS.has(key)) {
      if (value) node.setAttribute(key, "");
      else node.removeAttribute(key);
      continue;
    }
    // Default path: assign as property when possible, fall back to attribute.
    const descriptor = Object.getOwnPropertyDescriptor(node, key);
    if (descriptor && typeof descriptor.set === "function") {
      (node as unknown as Record<string, unknown>)[key] =
        typeof value === "boolean" ? value : value;
    } else {
      node.setAttribute(key, String(value));
    }
  }
}
